/* Skarpenord: the system as living React replicas – no screenshots – inside the proposal's browser frame.
   HeroShot – Hjem in the hero · SystemShots – six views with tabs, plays by itself,
   a click takes over · WorkspaceShot – under the AI section: the valve list read, then the proposals.
   The same components lay themselves out for three widths (full / compact / narrow); the Stage scales them to fit. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Calculator, FileText, Home, LayoutList, Table2, Workflow } from "lucide-react";
import { Inert, Stage } from "../Stage";
import { Shell } from "./SkShell";
import { HomeView } from "./SkViews";
import { IntakeApp } from "./SkIntake";
import { SCRIPT, SkSakApp } from "./SkSak";

const ICONS = { ventiler: Table2, visuelt: Workflow, konfig: LayoutList, kalkyle: Calculator, tilbud: FileText, hjem: Home };
const VIEW_MS = 7000;
const MANUAL_MS = 30000;

/* which moment of the case each view shows */
const BEAT = {
  oversikt: SCRIPT[0],
  ventiler: SCRIPT[1],
  visuelt: { ...SCRIPT[1], toast: null },
  konfig: SCRIPT[2],
  omfang: { ...SCRIPT[2], toast: null },
  kalkyle: SCRIPT[3],
  tilbud_utkast: SCRIPT[4],
  tilbud: SCRIPT[5],
};

/* one view of the system, laid out for `mode`. k: hjem · ny0 (the file attached) · ny (the findings) · a case tab (tilbud_utkast = the draft) */
export const SkView = ({ k, mode = "full", active = true, ns = "sk-sak", toast = true }) => {
  if (k === "hjem") {
    return (
      <Shell mode={mode} active="hjem" title="Hjem" testId="sk-view-hjem" view="hjem">
        <HomeView beat={active ? 1 : 0} mode={mode} />
      </Shell>
    );
  }
  if (k === "ny" || k === "ny0") return <IntakeApp at={k === "ny" ? 1 : 0} mode={mode} />;
  const beat = BEAT[k] || BEAT.oversikt;
  const tab = k.split("_")[0];
  return <SkSakApp beat={toast ? beat : { ...beat, toast: null }} tab={tab} mode={mode} ns={ns} panel={tab !== "visuelt"} />;
};

export const HeroShot = ({ d }) => (
  <Stage url={d.shots.host} caption={d.ui.prototype} height={820} narrowHeight={760} widths={{ fullWidth: 1360 }} testId="tilbod-hero-still" attrs={{ "data-view": "hjem" }}>
    {({ mode, active }) => (
      <Inert className="h-full w-full">
        <SkView k="hjem" mode={mode} active={active} />
      </Inert>
    )}
  </Stage>
);

/* one view at a time while in view; a click on a tab takes over for a while, then it plays on */
const useScript = (n, active) => {
  const [st, setSt] = useState({ v: 0, loop: 0 });
  const [paused, setPaused] = useState(false);
  const manualUntil = useRef(0);
  useEffect(() => {
    if (!active || paused || n < 2) return undefined;
    const t = setTimeout(() => setSt((s) => (manualUntil.current > Date.now() ? { v: s.v, loop: s.loop + 1 } : { v: (s.v + 1) % n, loop: s.loop + 1 })), VIEW_MS);
    return () => clearTimeout(t);
  }, [st, active, paused, n]);
  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, loop: s.loop + 1 }));
  }, []);
  return { ...st, paused, setPaused, select };
};

const Tabs = ({ views, current, loop, paused, onSelect }) => {
  const refs = useRef([]);
  const strip = useRef(null);
  useEffect(() => {
    const el = refs.current[current];
    const s = strip.current;
    if (!el || !s || s.scrollWidth <= s.clientWidth + 1) return;
    const left = el.offsetLeft - (s.clientWidth - el.offsetWidth) / 2;
    s.scrollTo({ left: Math.max(0, Math.min(left, s.scrollWidth - s.clientWidth)), behavior: "smooth" });
  }, [current]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : -1) + views.length) % views.length;
    onSelect(n);
    refs.current[n]?.focus();
  };
  return (
    <div ref={strip} className="kvl-switch" role="tablist" aria-label="Visning" data-paused={paused ? "true" : "false"} data-testid="tilbod-system-tabs">
      {views.map((v, i) => {
        const Icon = ICONS[v.key] || Home;
        const on = i === current;
        return (
          <button key={v.key} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1} className="kvl-switch-tab" onClick={() => onSelect(i)} onKeyDown={(e) => onKey(e, i)} data-testid={`tilbod-system-tab-${v.key}`}>
            <span className="kvl-switch-ic"><Icon aria-hidden="true" /></span>
            <span className="kvl-switch-txt"><b>{v.label}</b>{v.hint && <small>{v.hint}</small>}</span>
            {on && <i key={`${v.key}-${loop}`} className="kvl-switch-bar" style={{ "--dur": `${VIEW_MS}ms` }} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
};

export const SystemShots = ({ d }) => {
  const views = d.idea.views;
  const [active, setActive] = useState(false);
  const { v, loop, paused, setPaused, select } = useScript(views.length, active);
  const cur = views[v];
  return (
    <figure className="kvl-figure kvl-figure-mock" data-testid="tilbod-idea-figure" data-view={cur.key}>
      <Tabs views={views} current={v} loop={loop} paused={paused || !active} onSelect={select} />
      <div className="kvl-frame" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <Stage url={`${d.shots.host}${cur.url}`} caption={d.ui.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1360 }} testId="tilbod-system-mockup" attrs={{ "data-view": cur.key }} onActive={setActive}>
          {({ mode }) => (
            <Inert className="h-full w-full">
              <div key={cur.key} className="mb-view-enter h-full w-full" data-testid={`tilbod-system-view-${cur.key}`}>
                <SkView k={cur.key} mode={mode} ns="sk-sys" />
              </div>
            </Inert>
          )}
        </Stage>
      </div>
      <figcaption key={cur.key} className="kvl-cap-enter" data-testid="tilbod-idea-caption">{cur.caption}</figcaption>
    </figure>
  );
};

/* under the AI section: the valve list is read (Nytt arbeid → findings), then the proposals per valve (Konfigurasjon) */
export const WorkspaceShot = ({ d }) => {
  const ex = d.ai.example;
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % 2), 6500);
    return () => clearTimeout(t);
  }, [i, active]);
  const k = i === 0 ? "ny" : "konfig";
  return (
    <Stage url={`${d.shots.host}${k === "ny" ? "/ny" : ex.url}`} caption={d.ui.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1360 }} testId="tilbod-workspace-mockup" attrs={{ "data-view": k }} onActive={setActive}>
      {({ mode }) => (
        <Inert className="h-full w-full">
          <div key={k} className="mb-view-enter h-full w-full" data-testid={`tilbod-workspace-view-${k}`}>
            <SkView k={k} mode={mode} ns="sk-work" toast={false} />
          </div>
        </Inert>
      )}
    </Stage>
  );
};
